import { memo, SyntheticEvent, useContext, useState } from "react";

import { useMutation } from "@apollo/client";
import { useLottie } from "lottie-react";

import { FAVOURITES_ICON_SOURCE } from "../../app/constants/constants";
import { ADD_RECIPE_TO_FAVORITE, REMOVE_RECIPE_FROM_FAVORITE } from "../../app/graphql/mutations";
import { addUserFavorites, removeUserFavorites } from "../../app/homepage/homepage.slice";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import { EmptyHeart, FilledHeart } from "../../iconRef/recipePageIcons";
import { ErrorManagementContext } from "../../utils/errorManagement/ErrorManagementProvider";
import { logEvents } from "../../utils/logging";
import { FavouritesLottie } from "../../utils/lottieRefs";
import { Props } from "./favourites.types";
import styles from './favouritesViews.module.css';

const handleFavoriteClick = async (e: SyntheticEvent, props: Props, location: string, handleCustomAction?: () => void) => {
  const { recipeId, user, device, isUserFavorite, dispatch, removeUserFavoriteRecipes, addUserFavoriteRecipes, cookClubLog } = props;
  const userId = user?.id;
  const { displayName: deviceName, _id: deviceId } = device ?? {};

  e?.stopPropagation()
  if (handleCustomAction) handleCustomAction();
  if (isUserFavorite) {
    dispatch(removeUserFavorites(recipeId))
    try {
      await removeUserFavoriteRecipes({
        variables: { recipeId, userId }
      })
      logEvents("mobile-app", "APP_REMOVED_FAVOURITE", {
        deviceId: deviceName ?? deviceId,
        userId,
        recipeId,
        eventData: { recipeId, location }
      });
    }
    catch (err) {
      cookClubLog(`Could not remove this recipe from favorites. Please try again.`, "", {}, { ui: { type: "info" }, browserConsole: true })
      dispatch(addUserFavorites(recipeId))
    }
  } else {
    dispatch(addUserFavorites(recipeId))
    try {
      await addUserFavoriteRecipes({
        variables: { recipeId, userId }
      })
      logEvents("mobile-app", "APP_ADDED_FAVOURITE", {
        deviceId: deviceName ?? deviceId,
        userId,
        recipeId,
        eventData: { recipeId, location }
      });
    }
    catch (err) {
      cookClubLog(`Could not add this recipe to favorites. Please try again.`, "", {}, { ui: { type: "info" }, browserConsole: true })
      dispatch(removeUserFavorites(recipeId))
    }
  }
}

const Favorites = memo(({
  recipeId,
  stroke,
  size,
  source,
  handleCustomAction
}: {
  recipeId: string,
  stroke: string,
  size: number,
  source: string,
  handleCustomAction?: () => void
}) => {
  const [showAnimation, setShowAnimation] = useState(false)
  const user = useAppSelector((state) => state.main.user);
  const device = useAppSelector((state) => state.main.device);
  const favoriteRecipeIds = useAppSelector((state) => state.homepage.userFavoriteRecipeIds);
  const dispatch = useAppDispatch();
  const [removeUserFavoriteRecipes] = useMutation(REMOVE_RECIPE_FROM_FAVORITE)
  const [addUserFavoriteRecipes] = useMutation(ADD_RECIPE_TO_FAVORITE)
  const isUserFavorite = favoriteRecipeIds.includes(recipeId);
  const { cookClubLog } = useContext(ErrorManagementContext);

  const options = {
    animationData: FavouritesLottie,
    loop: !showAnimation,
  };
  const { View } = useLottie(options);

  const props = { recipeId, user, device, favoriteRecipeIds, isUserFavorite, dispatch, removeUserFavoriteRecipes, addUserFavoriteRecipes, cookClubLog, source }

  return (
    <div onClick={(e) => { handleFavoriteClick(e, props, "recipe", handleCustomAction); setShowAnimation(!isUserFavorite); }}>
      {isUserFavorite ? (
        <div className={styles.favorite_icon} >
          <FilledHeart className={source === FAVOURITES_ICON_SOURCE.RATINGS_CARD ? styles.filled_heart_icon : ''} height={size} width={size} />
          {showAnimation && <div className={styles.animation}>{View}</div>}
        </div>
      ) : (
        <div>
          <EmptyHeart className={source === FAVOURITES_ICON_SOURCE.RATINGS_CARD ? styles.empty_heart_icon : ''} height={size} stroke={stroke}
            strokeWidth="1" width={size} />
        </div>
      )}
    </div>
  );
})

export default Favorites;
